import React, { useEffect, useState } from "react";
import { Lock, ShieldCheck } from "lucide-react";

import { login, getToken } from "../lib/auth";

const CPS_LOGO_URL =
  "https://customer-assets.emergentagent.com/job_workshops-wind-data/artifacts/lvwu5z7c_CPS_Trans_400.png";

/**
 * Shared-password gate. Shown whenever there is no valid session token.
 * Not a full auth system — this is an access code gate with audit logging.
 */
export default function AuthGate({ children }) {
  const [authed, setAuthed] = useState(!!getToken());
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Re-check on storage changes (e.g. logout in another tab)
  useEffect(() => {
    function onStorage() {
      setAuthed(!!getToken());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(password.trim());
      setAuthed(true);
    } catch (err) {
      setError(err.message || "Invalid password");
    } finally {
      setBusy(false);
    }
  }

  if (authed) return children;

  return (
    <div
      className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4"
      data-testid="auth-gate"
    >
      <div className="w-full max-w-lg bg-white border border-gray-200 shadow-sm">
        <div className="border-b border-gray-100 px-6 py-5 flex items-center gap-4">
          <img
            src={CPS_LOGO_URL}
            alt="Community Power Solutions"
            className="h-16 w-auto object-contain flex-shrink-0"
          />
          <div>
            <h1 className="text-lg font-bold tracking-tight text-gray-900 whitespace-nowrap">
              Community Engagement Insights
            </h1>
            <p className="text-xs text-gray-600">
              Access required
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div>
            <label
              htmlFor="password"
              className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5"
            >
              Access password
            </label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
              <input
                id="password"
                data-testid="auth-password-input"
                type="password"
                autoFocus
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/30 focus:border-[#2F4F4F]"
                placeholder="Enter the password shared with you"
              />
            </div>
          </div>

          {error ? (
            <p
              className="text-xs text-rose-800 bg-rose-50 border border-rose-200 px-3 py-2"
              data-testid="auth-error"
            >
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={busy || !password}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#2F4F4F] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#1A3636] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="auth-submit"
          >
            <ShieldCheck className="h-4 w-4" />
            {busy ? "Verifying…" : "Enter dashboard"}
          </button>

          <p className="text-[11px] text-gray-500 leading-snug">
            This is a private analysis tool. Access is logged for audit
            purposes including date/time, IP address and browser.
          </p>
        </form>
      </div>
    </div>
  );
}
